import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, User, Mail, Phone, Camera, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { api, ApiError } from '../lib/api';
import { useToast } from '../components/Toast';
import type { MeUpdateInput } from '@shared/types';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

export function ModificaProfiloScreen() {
  const navigate = useNavigate();
  const { user, applyMe } = useAuth();
  const { show } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(user?.display_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatar_url ?? null);
  const [avatarKey, setAvatarKey] = useState<string | null | undefined>(undefined); // undefined = invariato
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const emailChanged = email.trim().toLowerCase() !== (user?.email ?? '');
  const initial = (name || '?').charAt(0).toUpperCase();

  const onPickAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_AVATAR_BYTES) return show('Immagine troppo grande (max 5 MB)');
    setUploading(true);
    try {
      const square = await cropSquare(file);
      setAvatarPreview(URL.createObjectURL(square));
      const res = await api.uploadFile(new File([square], 'avatar.jpg', { type: 'image/jpeg' }));
      setAvatarKey(res.file_key);
      show('Immagine caricata');
    } catch {
      show('Caricamento immagine non riuscito');
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    const cleanName = name.trim();
    if (!cleanName) return show('Inserisci nome e cognome');
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) return show('Email non valida');

    // Invia SOLO i campi cambiati.
    const patch: MeUpdateInput = {};
    if (cleanName !== (user?.display_name ?? '')) patch.display_name = cleanName;
    if (emailChanged) patch.email = cleanEmail;
    if ((phone.trim() || null) !== (user?.phone ?? null)) patch.phone = phone.trim() || null;
    if (avatarKey !== undefined) patch.avatar_key = avatarKey;

    if (Object.keys(patch).length === 0) {
      show('Nessuna modifica');
      return navigate(-1);
    }
    setSaving(true);
    try {
      const updated = await api.updateMe(patch);
      applyMe(updated);
      show(patch.email ? 'Salvato · verifica la nuova email' : 'Modifiche salvate');
      navigate(-1);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'email_taken') show('Email già in uso');
      else show(e instanceof ApiError ? e.message : 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="topbar__title">Modifica profilo</div>
        <span style={{ width: 40 }} />
      </div>

      {/* Avatar editor */}
      <div className="avatar-editor" style={{ marginBottom: 26 }}>
        <input ref={fileInput} type="file" accept="image/jpeg,image/png" hidden onChange={onPickAvatar} />
        <button type="button" className="avatar-editor__wrap" onClick={() => fileInput.current?.click()} aria-label="Cambia immagine">
          {avatarPreview ? (
            <img className="avatar-editor__img" src={avatarPreview} alt="" />
          ) : (
            <span className="avatar-editor__img">{initial}</span>
          )}
          <span className="avatar-editor__badge">
            <Camera size={16} />
          </span>
        </button>
        <button type="button" onClick={() => fileInput.current?.click()} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, fontWeight: 600 }}>
          {uploading ? 'Caricamento…' : 'Cambia immagine'}
        </button>
        <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: -6 }}>JPG o PNG · max 5 MB</span>
      </div>

      {/* Nome e cognome */}
      <label className="field__label" htmlFor="name">Nome e cognome</label>
      <div className="inputrow" style={{ marginBottom: 18 }}>
        <User size={18} />
        <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario Rossi" autoComplete="name" />
      </div>

      {/* Email */}
      <label className="field__label" htmlFor="email">Indirizzo email</label>
      <div className="inputrow">
        <Mail size={18} />
        <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario.rossi@email.it" autoComplete="email" />
      </div>
      <div className="verify-row">
        {emailChanged ? (
          <>
            <AlertCircle size={13} style={{ color: 'var(--adim)' }} />
            <span>Da verificare dopo il salvataggio</span>
          </>
        ) : user?.email_verified ? (
          <>
            <CheckCircle2 size={13} style={{ color: 'var(--accent)' }} />
            <span>Email verificata</span>
          </>
        ) : (
          <>
            <AlertCircle size={13} style={{ color: 'var(--adim)' }} />
            <span>Email da verificare</span>
          </>
        )}
      </div>

      {/* Telefono */}
      <label className="field__label" htmlFor="phone" style={{ marginTop: 22 }}>
        Telefono <span style={{ opacity: 0.6 }}>(facoltativo)</span>
      </label>
      <div className="inputrow">
        <Phone size={18} />
        <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 ___ ___ ____" autoComplete="tel" />
      </div>

      {/* Action bar */}
      <div className="actionbar">
        <button className="btn" style={{ flex: 1 }} onClick={() => navigate(-1)} disabled={saving}>
          Annulla
        </button>
        <button className="btn btn--accent" style={{ flex: 1.4 }} onClick={onSave} disabled={saving || uploading}>
          {saving ? 'Salvataggio…' : 'Salva modifiche'}
        </button>
      </div>
    </div>
  );
}

/** Ritaglio quadrato centrato + resize a 512px, output JPEG. */
async function cropSquare(file: File, size = 512): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - side) / 2;
  const sy = (bitmap.height - side) / 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas non disponibile');
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, size, size);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('crop fallito'))), 'image/jpeg', 0.9),
  );
}
